/**
 * The "working clinic" — which single clinic an admin's clinic-scoped actions
 * act on (manual eligibility check, CSV upload, Doctors, Payer Mappings).
 *
 * This replaces `organizations/{orgId}.defaultClinicId`, which was a piece of
 * UI state stored in the data model. Nothing on the backend ever authorized off
 * it — it only seeded the SPA's starting clinic — but because it was a
 * denormalized pointer that no lifecycle maintained, archiving the clinic it
 * named left every admin in the org locked out with "Clinic is not active."
 *
 * The replacement keeps the choice where it belongs: per-admin, in the browser,
 * validated against the live active-clinic list on every load. A remembered
 * clinic that has since been archived (or that the admin no longer has) simply
 * falls back — it can never wedge anyone, and it can never go stale, because
 * nothing durable stores it.
 *
 * Non-admins never reach this: their `clinicId` claim IS their working clinic.
 */

const KEY_PREFIX = 'vitalrev:workingClinic'

function storageKey(orgId: string, uid: string): string {
  return `${KEY_PREFIX}:${orgId}:${uid}`
}

/**
 * Remembered working clinic for this admin in this org, or null.
 *
 * Scoped by uid as well as org so a shared browser doesn't leak one operator's
 * clinic choice into another's session.
 */
export function readWorkingClinic(orgId: string, uid: string): string | null {
  try {
    return localStorage.getItem(storageKey(orgId, uid))
  } catch {
    // Safari private mode / storage disabled — fall back to derivation.
    return null
  }
}

/** Remember this admin's working clinic. Best-effort; never throws. */
export function writeWorkingClinic(orgId: string, uid: string, clinicId: string): void {
  try {
    localStorage.setItem(storageKey(orgId, uid), clinicId)
  } catch {
    // Storage unavailable — the choice just doesn't survive the session.
  }
}

/**
 * Resolve the clinic a user's clinic-scoped actions act on.
 *
 * Precedence:
 *   1. `assignedClinicId` — a manager/user's own clinic claim, always wins.
 *   2. `storedClinicId` — the admin's remembered choice, IF it's still active.
 *   3. First active clinic — first run, or the remembered one went away.
 *   4. null — the org has no active clinic; callers must render an empty state
 *      rather than send `undefined` to a clinic-scoped callable.
 *
 * `activeClinics` must already be filtered to active (`listClinics` is), and
 * order decides the fallback, so pass it in a stable order.
 *
 * Pure — the localStorage side lives in the helpers above.
 */
export function resolveWorkingClinic(args: {
  assignedClinicId: string | null
  storedClinicId: string | null
  activeClinics: readonly { id: string }[]
}): string | null {
  const { assignedClinicId, storedClinicId, activeClinics } = args

  if (assignedClinicId) return assignedClinicId

  if (storedClinicId && activeClinics.some(c => c.id === storedClinicId)) {
    return storedClinicId
  }

  return activeClinics[0]?.id ?? null
}

/**
 * What to do about the working clinic before running a manual check on a
 * Cockpit row.
 *
 *   ok           — proceed as-is: the row is already in the working clinic, or
 *                  it carries no clinic (a legacy row that predates clinic
 *                  stamping; the server's clinic-access check still governs).
 *   switch       — move the working clinic to the row's before launching.
 *   unavailable  — the row's clinic is not active, so it cannot be switched to
 *                  and the check must not run. The CALLER must refuse.
 */
export type RowClinicDecision =
  | { kind: 'ok' }
  | { kind: 'switch'; clinicId: string }
  | { kind: 'unavailable'; clinicId: string }

/**
 * Decide the working clinic for a manual check launched from a Cockpit row.
 *
 * An admin's Cockpit spans the whole org while the working clinic is one
 * separate choice, so a row from another clinic is ordinary. The check is
 * created under the working clinic and the row's first eligibility link is
 * written by path (`clinics/{clinicId}/appointments/{id}`), so a mismatch
 * attributes the check to the wrong clinic AND writes the link to a document
 * that does not exist — the row keeps reading "Cannot check" after staff have
 * already answered it. Non-admins never differ: their claim is their working
 * clinic and their Cockpit is scoped to it.
 *
 * `activeClinics` is required because a switch is a REQUEST, not a guarantee:
 * `resolveWorkingClinic` only honours a stored clinic that is still active, so
 * naming an archived one silently resolves to `activeClinics[0]` instead — the
 * check then runs under a third clinic and the link lands nowhere, which is
 * precisely the failure this function exists to prevent. The admin cockpit
 * reads an org-wide collection group with no clinic-status filter, so archived
 * clinics' rows genuinely reach it. Returning `unavailable` makes the caller
 * refuse out loud instead.
 */
export function clinicSwitchForRow(
  rowClinicId: string | null | undefined,
  workingClinicId: string | null,
  activeClinics: readonly { id: string }[],
): RowClinicDecision {
  if (!rowClinicId) return { kind: 'ok' }
  if (rowClinicId === workingClinicId) return { kind: 'ok' }
  if (!activeClinics.some(c => c.id === rowClinicId)) {
    return { kind: 'unavailable', clinicId: rowClinicId }
  }
  return { kind: 'switch', clinicId: rowClinicId }
}
